import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import PublicLayout from "@/components/PublicLayout";
import AccountLayout from "@/components/AccountLayout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/AuthExtras";
import Sports, { MatchDetail } from "@/pages/Sports";
import Casino from "@/pages/Casino";
import Promotions, { Vip, Legal, Faq } from "@/pages/Content";
import Overview from "@/pages/account/Overview";
import Profile from "@/pages/account/Profile";
import WalletPage from "@/pages/account/Wallet";
import Deposit from "@/pages/account/Deposit";
import Withdraw from "@/pages/account/Withdraw";
import Transactions from "@/pages/account/Transactions";
import Settings from "@/pages/account/Settings";
import { Bets, Bonuses, Notifications, Support } from "@/pages/account/AccountMisc";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard, { AdminAudit, AdminTickets } from "@/pages/admin/AdminPages";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import AdminContent from "@/pages/admin/AdminContent";
import AdminLogin from "@/pages/admin/AdminLogin";
import SuperAdmin from "@/pages/admin/SuperAdmin";
import Arcade from "@/pages/arcade/Arcade";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminCricket from "@/pages/admin/AdminCricket";
import AdminFantasy from "@/pages/admin/AdminFantasy";
import AdminExchange from "@/pages/admin/AdminExchange";
import Fantasy, { FantasyMatch, TeamBuilder, ContestView } from "@/pages/fantasy/Fantasy";

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const NotFound = () => <main className="grid min-h-[50vh] place-items-center text-center"><div><p className="font-display text-6xl font-black text-cr-gold">404</p><p className="mt-2 text-cr-muted">Page not found</p></div></main>;

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollTop />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Landing />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/sports" element={<Sports />} />
              <Route path="/sports/results" element={<Sports initialStatus="finished" />} />
              <Route path="/sports/live" element={<Sports initialStatus="live" />} />
              <Route path="/sports/upcoming" element={<Sports initialStatus="upcoming" />} />
              <Route path="/sports/match/:id" element={<MatchDetail />} />
              <Route path="/casino" element={<Casino section="casino" />} />
              <Route path="/live-casino" element={<Casino section="live" />} />
              <Route path="/arcade" element={<Arcade />} />
              <Route path="/fantasy" element={<Fantasy />} />
              <Route path="/fantasy/contest/:cid" element={<ContestView />} />
              <Route path="/fantasy/:fid" element={<FantasyMatch />} />
              <Route path="/fantasy/:fid/create" element={<TeamBuilder />} />
              <Route path="/promotions" element={<Promotions />} />
              <Route path="/vip" element={<Vip />} />
              <Route path="/legal/:slug" element={<Legal />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path="/account" element={<AccountLayout />}>
              <Route index element={<Overview />} />
              <Route path="profile" element={<Profile />} />
              <Route path="wallet" element={<WalletPage />} />
              <Route path="deposit" element={<Deposit />} />
              <Route path="withdraw" element={<Withdraw />} />
              <Route path="transactions" element={<Transactions />} />
              <Route path="bets" element={<Bets />} />
              <Route path="bonuses" element={<Bonuses />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="support" element={<Support />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="/admin" element={<AdminLayout mode="admin" />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="cricket" element={<AdminCricket />} />
              <Route path="fantasy" element={<AdminFantasy />} />
              <Route path="live-bets" element={<AdminExchange />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="tickets" element={<AdminTickets />} />
              <Route path="audit" element={<AdminAudit />} />
            </Route>
            <Route path="/super-admin/login" element={<AdminLogin mode="super" />} />
            <Route path="/super-admin" element={<AdminLayout mode="super" />}>
              <Route index element={<SuperAdmin />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="cricket" element={<AdminCricket />} />
              <Route path="fantasy" element={<AdminFantasy />} />
              <Route path="live-bets" element={<AdminExchange />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="tickets" element={<AdminTickets />} />
              <Route path="audit" element={<AdminAudit />} />
            </Route>
          </Routes>
          <Toaster theme="dark" position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
